import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

/**
 * Whether a comment or suggestion should offer the unified batch controls ("Add to batch" /
 * "Commit suggestions") instead of the legacy ones ("Add suggestion to batch" / "Apply
 * suggestion").
 *
 * `unified_batch_pr_comments` can be enabled for a whole repository or organization, but the
 * unified batch itself only works in the new Files Changed experience. A viewer whose project has
 * the flag while they are still on the classic Files Changed page would otherwise get unified
 * controls they cannot follow through on, and lose the legacy batch flow that still works for
 * them.
 *
 * The Rails controller only adds `react_data_router_pull_request_files` to the app payload when
 * the new Files Changed experience is enabled for the viewer — it is added by
 * `PullRequests::ReactDependency#add_react_feature_flags` under a block that returns
 * `new_files_changed_feature_enabled_for_user?`, and that is the only place in `github/github`
 * that adds it — which makes it the signal for "the unified batch is reachable". It is an app
 * payload flag rather than a client env one, so it is read with `useFeatureFlag` and is not
 * available through `isFeatureEnabled`. `header-tabs` and `ReviewRequestBanner` read it the same
 * way for the same question.
 *
 * Rails-rendered React partials mount their own root through `PartialEntry`, which does not
 * provide `AppPayloadContext`, so this reads `false` there and keeps the classic experience on
 * the legacy controls.
 *
 * This deliberately gates only where a batch is *created*. The surfaces that manage an existing
 * batch (header button, Manage batch panel, the Overview "batch in progress" item) stay on the
 * plain flag so a viewer who switches back to classic Files Changed can still finish a batch they
 * already started.
 */
export function useIsUnifiedBatchEnabled(): boolean {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     `react_data_router_pull_request_files` is an app payload flag, not a client env one, so it is
     not available through `isFeatureEnabled`. */
  const newFilesChangedEnabled = useFeatureFlag('react_data_router_pull_request_files')

  return isFeatureEnabled('unified_batch_pr_comments') && newFilesChangedEnabled
}
